import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { blogTranslations } from "@/utils/translations/blog";
import { localizePath } from "@/utils/languageRoutes";
import { getInsightPath } from "@/utils/insightSlugs";
import type { Insight } from "@/types/insight";
import { getThumbnailUrl } from "@/utils/imageUtils";
import { SectionHeading, EASE } from "./demo-shared";

interface DemoInsightsProps {
  language: string;
}

const DemoInsights = ({ language }: DemoInsightsProps) => {
  const isKo = language === "KO";
  const t = blogTranslations[language as keyof typeof blogTranslations] || blogTranslations.EN;

  const { data: insights } = useQuery({
    queryKey: ["demo-latest-insights"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("insights")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(4);
      if (error) throw error;
      return data as Insight[];
    },
  });

  if (!insights?.length) return null;

  return (
    <section className="relative bg-[#FAFAF8] py-24 md:py-32">
      <div className="container mx-auto max-w-5xl px-4">
        <SectionHeading no="06" eyebrow="Insights" title={t.latestSection.title} sub={t.latestSection.subtitle} />

        <div className="mt-14">
          {insights.map((insight, index) => {
            const title = isKo && insight.title_ko ? insight.title_ko : insight.title_en;
            const description = isKo ? insight.description_ko : insight.description_en;
            const thumbnail = insight.thumbnail_url;
            const date = insight.published_at
              ? new Date(insight.published_at).toLocaleDateString(isKo ? "ko-KR" : "en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "";
            return (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: index * 0.06, ease: [...EASE] }}
              >
                <Link
                  to={getInsightPath(insight, language)}
                  className="group grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 border-t border-slate-200 py-6 transition-colors first:border-t-0 hover:bg-white md:-mx-6 md:gap-6 md:rounded-2xl md:px-6"
                >
                  <span className="font-mono text-sm font-semibold text-slate-300 transition-colors group-hover:text-secondary">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-slate-900 transition-colors group-hover:text-primary md:text-lg">
                      {title}
                    </h3>
                    <p className="mt-1 hidden truncate text-sm text-slate-500 md:block">{description}</p>
                    <p className="mt-1 text-xs text-slate-400">{date}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {thumbnail && (
                      <img
                        src={getThumbnailUrl(thumbnail)}
                        alt=""
                        aria-hidden
                        className="hidden h-14 w-20 rounded-lg object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:block"
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <ArrowUpRight className="h-5 w-5 text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 text-center"
        >
          <Link
            to={localizePath("/insights", language)}
            className="group inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-all hover:border-primary/30 hover:text-primary"
          >
            {isKo ? "모든 인사이트 보기" : "View all insights"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoInsights;
